/**
 * Admin — Geographies
 *
 * Full-page wrapper around the GeographyAdminModal component,
 * rendered in inline (non-modal) mode within the admin console.
 *
 * The geography component uses CSS custom properties (var(--text-primary), etc.)
 * designed for the app's dark theme. The admin console has a light background,
 * so we override those properties here to force light-theme values.
 *
 * English only (system admin UI).
 */

import React from 'react';
import GeographyAdminModal from '../../Components/GeographyAdminModal/GeographyAdminModal';
import styles from './AdminPages.module.css';

/**
 * Light-theme overrides for the CSS custom properties used by GeographyAdminModal.
 * These ensure readable contrast against the admin console's #f4f5f7 background.
 */
const LIGHT_THEME_OVERRIDES: React.CSSProperties = {
  // @ts-ignore — CSS custom properties are valid inline styles at runtime
  '--text-primary': '#020617',
  '--text-secondary': '#334155',
  '--text-tertiary': '#475569',
  '--text-muted': '#64748b',
  '--bg-primary': '#ffffff',
  '--bg-secondary': '#f8fafc',
  '--bg-tertiary': '#ffffff',
  // ^ DEV-939 followup: SlugField (and any other shared input that
  //   uses --bg-tertiary for its field background) renders inside the
  //   admin console's light page bg. Without this override the input
  //   inherits the global dark-theme tertiary (#0f172a-ish) and reads
  //   as a black box on a white page — Skip caught it on the geo
  //   slug edit form.
  '--bg-hover': 'rgba(0, 0, 0, 0.03)',
  '--bg-hover-strong': 'rgba(0, 0, 0, 0.06)',
  '--border-default': 'rgba(2, 6, 23, 0.15)',
  '--border-subtle': 'rgba(2, 6, 23, 0.08)',
  '--success-bg': 'rgba(22, 163, 74, 0.10)',
  '--error-bg': 'rgba(220, 38, 38, 0.08)',
  '--accent-primary': '#2563eb',
  '--accent-text': '#1d4ed8',
} as React.CSSProperties;

export default function AdminGeographies() {
  return (
    <div className={styles.page} style={LIGHT_THEME_OVERRIDES}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Geographies</h1>
        <p className={styles.pageSubtitle}>
          System-level geographic hierarchy — Earth → Countries → States → Cities.
          Active geographies generate community pages. Inactive geographies serve as location labels only.
        </p>
      </div>
      <GeographyAdminModal open={true} onClose={() => {}} mode="inline" />
    </div>
  );
}
